import { useCallback, useEffect } from "react";
import { MembersGroup, CreateMembersGroup, UpdateMembersGroup } from "../types/models/MembersGroup.d";
import { UpdateEntityMembersGroup } from "../types/models/MembersGroup";
import { useGetMembersGroupListQuery, useInsertMembersGroupMutation, useUpdateMembersGroupMutation, useDeleteMembersGroupMutation, } from "../apis/MembersGroup.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useMembersGroup(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertMembersGroupMutation, { 
        isLoading: insertMembersGroupLoading, 
        isSuccess: insertMembersGroupIsSuccess,
        isError: insertMembersGroupIsError,
        error: insertMembersGroupError,
    }] = useInsertMembersGroupMutation()
    const [updateMembersGroupMutation, { 
        isLoading: updateMembersGroupLoading,
        isSuccess: updateMembersGroupIsSuccess,
        isError: updateMembersGroupIsError,
        error: updateMembersGroupError,
    }] = useUpdateMembersGroupMutation()
    const [deleteMembersGroupMutation, { 
        isLoading: deleteMembersGroupLoading,
        isSuccess: deleteMembersGroupIsSuccess,
        isError: deleteMembersGroupIsError,
        error: deleteMembersGroupError,
    }] = useDeleteMembersGroupMutation()

    const loading =
        insertMembersGroupLoading ||
        updateMembersGroupLoading ||
        deleteMembersGroupLoading ||
        false

    const insertMembersGroup = useCallback(async (membersGroup: CreateMembersGroup, redirectPath: string|undefined) => {
        try {
            const data = await insertMembersGroupMutation(membersGroup).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertMembersGroupMutation, navigate])
    
    useEffect(() => {
        if (insertMembersGroupIsSuccess) {
            openSuccessAlert('Members Group created')
        } else if (insertMembersGroupIsError) {
            console.log("add error", insertMembersGroupError)
            openFailAlert(t`Members Group add error: ` + insertMembersGroupError?.data?.message)
        }
    }, [
        insertMembersGroupIsSuccess,
        insertMembersGroupIsError,
        insertMembersGroupError
    ]);

    const updateMembersGroup = useCallback(async (membersGroup: UpdateEntityMembersGroup, redirectPath: string|undefined) => {
        try {
            const data = await updateMembersGroupMutation(membersGroup).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateMembersGroupMutation, navigate])
    
    useEffect(() => {
        if (updateMembersGroupIsSuccess) {
            openSuccessAlert(t`Members Group updated`)
        } else if (updateMembersGroupIsError) {
            console.log("update error", updateMembersGroupError)
            openFailAlert(t`Members Group update error: ` + updateMembersGroupError?.data?.message)
        }
    }, [
        updateMembersGroupIsSuccess,
        updateMembersGroupIsError,
        updateMembersGroupError
    ]);

    const deleteMembersGroup = useCallback((membersGroup: MembersGroup, redirectPath: string|undefined) => {
        openDialog(async () => {
            membersGroup = {
                ...membersGroup,
            }
            try {
                const { data } = await deleteMembersGroupMutation(membersGroup);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteMembersGroupMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteMembersGroupIsSuccess) {
            openSuccessAlert(t`Members Group deleted`)
        } else if (deleteMembersGroupIsError) {
            console.log("delete error", deleteMembersGroupError)
            openFailAlert(t`Members Group delete error: ` + deleteMembersGroupError?.data?.message)
        }
    }, [
        deleteMembersGroupIsSuccess,
        deleteMembersGroupIsError,
        deleteMembersGroupError
    ]);

    return {
        loading,
        insertMembersGroup,
        updateMembersGroup,
        deleteMembersGroup
    }
}
